import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "philosophertest_admin_pwd";

/**
 * Session-scoped admin auth hook.
 * - Stores the password in sessionStorage so it survives navigation but is
 *   cleared when the tab closes.
 * - NEVER touches URL query params — see security requirement.
 * - `verify(pwd, apiCall)` calls the given probe endpoint; on success the
 *   password is persisted and returned, otherwise cleared.
 */
export function useAdminSession() {
  const [password, setPassword] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });

  const login = useCallback(async (pwd, probe) => {
    // `probe` is an async function that takes the password and calls an
    // authenticated endpoint. It must throw on 401.
    await probe(pwd);
    try {
      sessionStorage.setItem(STORAGE_KEY, pwd);
    } catch {
      /* private-mode safari etc — keep in memory only */
    }
    setPassword(pwd);
    return pwd;
  }, []);

  const logout = useCallback(() => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setPassword(null);
  }, []);

  return { password, login, logout, isAuthed: !!password };
}

/** Small shared login form + logout button used by admin pages. */
export function AdminLoginForm({ onSubmit, busy, error, testidPrefix = "admin" }) {
  const [value, setValue] = useState("");

  const submit = (e) => {
    e.preventDefault();
    if (!value || busy) return;
    onSubmit(value);
  };

  return (
    <form onSubmit={submit} className="mt-6 flex max-w-md gap-2" data-testid={`${testidPrefix}-login-form`}>
      <input
        type="password"
        placeholder="Admin password"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        autoComplete="current-password"
        autoFocus
        required
        className="flex-1 rounded border px-3 py-3 text-[14px] outline-none"
        style={{
          background: "rgba(22,24,33,0.5)",
          borderColor: "rgba(154,151,164,0.25)",
          color: "var(--text)",
        }}
        data-testid={`${testidPrefix}-password-input`}
      />
      <button
        type="submit"
        className="btn-primary"
        style={{ width: "auto", padding: "12px 20px" }}
        disabled={busy || !value}
        data-testid={`${testidPrefix}-login-btn`}
      >
        {busy ? "…" : "Sign in"}
      </button>
      {error && (
        <p className="basis-full text-[13px]" style={{ color: "#e07070" }} data-testid={`${testidPrefix}-login-error`}>
          {error}
        </p>
      )}
    </form>
  );
}

export function LogoutButton({ onLogout, testid = "admin-logout-btn" }) {
  return (
    <button
      type="button"
      onClick={onLogout}
      className="btn-ghost"
      style={{ width: "auto", padding: "8px 14px", fontSize: "11px" }}
      data-testid={testid}
    >
      Log out
    </button>
  );
}

// Strip any legacy ?password= from the URL on mount — never leave secrets in history.
export function useStripPasswordFromUrl() {
  useEffect(() => {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("password")) {
        url.searchParams.delete("password");
        window.history.replaceState(null, "", url.pathname + (url.search || ""));
      }
    } catch {
      /* ignore */
    }
  }, []);
}
